import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBoolean } from "@/hooks/use-boolean";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useAddImportMethod from "@babackapp/private-api-client/client/use-add-import-method";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren } from "react";
import ImportSettingsForm from "./import-settings-form";
import { ShopImportFormValues } from "./import-settings-form-types";
import { mappingFormSchemaToMappingDto } from "../../lib/mapping-form-schema-to-mapping-dto.ts";

declare function checkFTPLogin(data: ShopImportFormValues): Promise<void>;

export default function AddImportDialog({ children }: PropsWithChildren) {
  const t = useTranslation();
  const open = useBoolean();
  const hasAdminRights = useViewerHasAtLeastAdminRights();
  const { data: shop } = useCurrentShopSuspense();

  const { mutateAsync: addImport } = useAddImportMethod(shop);

  const onSubmit = async (data: ShopImportFormValues) => {
    await addImport({
      shopImport: {
        login: data.server.login,
        mapping: mappingFormSchemaToMappingDto(data.mapping),
        settings: data.options.settings,
        type: data.server.type,
      },
    });
    return open.setValue(false);
  };

  const onValidate = (values: ShopImportFormValues) => {
    return checkFTPLogin(values);
  };

  void onValidate;

  if (!hasAdminRights) {
    return null;
  }

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[600px]" onPointerDownOutside={(ev) => ev.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {t("settings.integrations.imports.addImport.serverType")}
          </DialogTitle>
          <DialogDescription>
            {t("settings.integrations.imports.addImport.description")}
          </DialogDescription>
        </DialogHeader>
        <ImportSettingsForm onValidate={onValidate} onSubmit={onSubmit} />
      </DialogContent>
    </Dialog>
  );
}
